<template>
  <div class="pa-4">
    <div v-if="activeOrder.id" class="shipping-title">
      <PageTitle title="Add Shipping Information" />

      <order-expiration-timer
        v-if="orderProduct.id"
        :order-product-id="orderProduct.id"
      ></order-expiration-timer>
    </div>
    <!-- // product data? -->
    <v-checkbox
      v-if="isGiftable"
      v-model="shipToOther"
      class="shipping-info-form__checkbox"
      color="primary"
      density="compact"
      hide-details
      label="Ship to Someone Else"
      true-icon="mdi-checkbox-marked"
      false-icon="mdi-checkbox-blank-outline"
    />
    <v-form
      ref="form"
      v-model="valid"
      class="shipping-info-form"
      validate-on="input"
    >
      <div v-for="(field, idx) in fields" :key="idx">
        <h4 class="ma-0 mb-3">{{ field.label }} 1</h4>
        <div v-for="(input, idx) in field.recipientData" :key="idx">
          <div v-if="input.name === 'phone'" class="pb-4">
            <phone-input
              v-model="input.val"
              :default-country="partnerCountry"
              :disabled="!shipToOther"
              :placeholder="input.label"
              @validate="phoneValid = $event.valid"
            />
          </div>

          <v-text-field
            v-else
            v-model="input.val"
            :ref="el => registerFieldRef(input.name, el)"
            class="shipping-info-form__input mb-1"
            :data-form-type="!shipToOther ? 'other' : ''"
            :data-lpignore="!shipToOther"
            density="compact"
            :disabled="!shipToOther"
            :label="input.label"
            :name="input.name"
            :readonly="!shipToOther"
            :rules="shipToOther ? input.rule : []"
            :type="input.type"
            variant="outlined"
            @update:model-value="handleFieldInput(input.name)"
          ></v-text-field>
        </div>
        <div v-for="(input, idx) in field.shippingData" :key="idx">
          <v-text-field
            v-if="input.type === 'input'"
            v-model="input.val"
            :ref="el => registerFieldRef(input.name, el)"
            class="shipping-info-form__input mb-1"
            density="compact"
            :label="input.label"
            :name="input.name"
            :rules="input.rule"
            :type="input.type"
            variant="outlined"
            @update:model-value="handleFieldInput(input.name)"
            @blur="valueChanged"
          ></v-text-field>
          <v-autocomplete
            v-if="input.type === 'select'"
            v-model="input.val"
            :ref="el => registerFieldRef(input.name, el)"
            class="shipping-info-form__input shipping-info-form__select mb-1"
            density="compact"
            :items="input.items"
            :label="input.label"
            item-title="text"
            item-value="value"
            :rules="input.rule"
            variant="outlined"
            @update:model-value="
              handleSelectInput(input, $event)
            "
            :menu-props="{ contentClass: 'shipping-info-form__select-menu' }"
          ></v-autocomplete>
        </div>
      </div>
    </v-form>
    <v-btn
      class="font-weight-bold white--text text-decoration-none rounded-pill shipping-title__btn"
      color="primary"
      :disabled="!isFormValid"
      size="large"
      variant="flat"
      width="100%"
      @click="reviewOrder"
    >
      Next: Review Order
    </v-btn>
    <error-modal
      v-if="modal.error"
      :value="modal.error"
      @close-modal="modal.error = false"
    />
  </div>
</template>

<script>
/* eslint-disable camelcase */
import { mapActions, mapGetters, mapState } from 'vuex';
import ErrorModal from '@/components/shared/ErrorModal/index.vue';
import PageTitle from '@/components/shared/PageTitle/index.vue';
import OrderExpirationTimer from '@/components/shared/OrderExpirationTimer/index.vue';
import { isInvalidZipCode, recipientFields, statesByCountry } from './data';
import PhoneInput from '@/components/shared/PhoneInput/index.vue';

export default {
  name: 'ShippingInfo',
  components: {
    ErrorModal,
    OrderExpirationTimer,
    PageTitle,
    PhoneInput
  },
  data() {
    return {
      fields: [],
      modal: {
        error: false
      },
      orderProduct: {},
      phoneValid: true,
      product: {},
      valid: false,
      shipToOther: false,
      submitting: false,
      fieldArrays: [],
      fieldRefs: {}
    };
  },
  computed: {
    ...mapGetters('cartOrders', {
      activeOrder: 'active'
    }),
    ...mapState(['offer', 'partner', 'partnerUser']),
    // A stored phone without a dial code (e.g. 8016780045) is read in the partner's country;
    // without it the input falls back to the first country in its list and rejects the number.
    partnerCountry() {
      return String(this.partner?.country || '').toUpperCase();
    },
    isGiftable() {
      return (
        (this.offer?.gifting && this.product?.giftable) ||
        this.$route.query.giftable
      );
    },
    isFormValid() {
      return this.valid === true && this.phoneValid && !this.submitting;
    }
  },
  methods: {
    ...mapActions('cartOrders', ['updateOrder', 'orderExpired', 'setShip']),
    registerFieldRef(name, el) {
      if (!name) return;
      if (el) this.fieldRefs[name] = el;
      else delete this.fieldRefs[name];
    },
    handleFieldInput(name) {
      const fieldRef = this.fieldRefs[name];
      if (fieldRef?.isValid === false && typeof fieldRef.validate === 'function')
        fieldRef.validate();
    },
    handleSelectInput(input, value) {
      if (input.triggerUpdate) this.triggerUpdate(input.triggerUpdate, value);
      this.handleFieldInput(input.name);
    },
    syncPhoneValidity() {
      const phoneField = this.fieldArrays.find(field => field.name === 'phone');
      const phoneValue = phoneField?.val != null ? String(phoneField.val).trim() : '';

      // Mobile phone is optional in shipping form. If empty, it should not block submit.
      if (!phoneValue) this.phoneValid = true;
    },
    // TODO look into making this reusable. possible mixin?
    timeExpired() {
      setTimeout(async () => {
        let moreTime = false;
        // eslint-disable-next-line no-alert, no-restricted-globals
        if (confirm('Would you like more time?') === true) moreTime = true;

        const res = await this.orderExpired({
          orderId: this.activeOrder.id,
          itemType: `order_${this.$route.params.type}s`,
          itemId: this.orderProduct.id,
          moreTime
        });

        if (res) {
          this.$router.push({
            name: 'Rewards',
            query: {
              t: this.$route.query.t
            }
          });
        } else this.$refs.orderTimer.startCountdown(true);
      }, 5);
    },
    async reviewOrder() {
      this.submitting = true;
      const { valid } = await this.$refs.form.validate();
      if (!valid) {
        this.submitting = false;
        return;
      }
      const customer = {};
      this.fieldArrays.forEach(item => (customer[item.name] = item.val));

      try {
        const res = await this.updateOrder({
          orderId: this.activeOrder.id,
          distribute: this.shipToOther ? customer : false,
          customer: this.shipToOther ? undefined : customer,
          partnerUser: this.partnerUser.id,
          itemId: this.orderProduct ? this.orderProduct.id : '',
          itemType: `order_${this.$route.params.type}s`,
          attributeId: this.attributeSelected
        });
        if (res.status === 200) {
          this.$router.push({
            name: 'ReviewOrder',
            query: {
              t: this.$route.query.t
            }
          });
        }
      } catch (error) {
        console.error('Failed to update the the order', error);
      }
      this.submitting = false;
    },

    setPartnerDataToShippingInfo() {
      // calls the form data to populate it. passing the data it needs to know about.
      this.fields = recipientFields(
        this.partnerUser,
        this.shipToOther,
        this.offer.zip_code_settings,
        isInvalidZipCode,
        this.product.id
      );
      // merging the data of each array.
      this.fieldArrays = [
        ...this.fields[0].recipientData,
        ...this.fields[1].shippingData
      ];
      // currently the email isnt being sent back. so i Manually pull it off to assign it.
      const emailField = this.fieldArrays.find(i => i.name === 'email');
      // plucking off the keys of partner user then assigning it with the value on later on.
      const customerKeys = Object.keys(this.activeOrder.customer);

      // Check and see if the order has been set to be distributed
      const distributed =
        'distribute' in this.activeOrder ? this.activeOrder.distribute : {};
      // if its set to distribute, we want to set the form data to be the distributed info.
      const distributedKeys = Object.keys(distributed);

      if (
        this.shipToOther &&
        distributedKeys.length > 0 &&
        !!distributed.email
      ) {
        // looping through the form data keys and finding the keys on the order then assigning the value
        this.fieldArrays.forEach(field =>
          distributedKeys.forEach(cItem => {
            if (field.name === cItem)
              field.val = this.activeOrder.distribute[cItem];
          })
        );
        if (distributed.email && this.shipToOther)
          emailField.val = distributed.email;
      }
      if (!this.shipToOther) {
        // looping through the form data keys and finding the keys on the order then assigning the value
        this.fieldArrays.forEach(field =>
          customerKeys.forEach(cItem => {
            if (field.name === cItem)
              field.val = this.activeOrder.customer[cItem];
          })
        );
        emailField.val = this.partnerUser.email;
      }
      const countryField = this.fieldArrays.find(i => i.name === 'country');
      if (countryField) {
        if (countryField.val !== 'US' && countryField.val !== 'CA')
          countryField.val = 'US';
        this.triggerUpdate('state', countryField.val);
      }
      const zipCodeField = this.fieldArrays.find(i => i.name === 'zip_code');
      if (zipCodeField) zipCodeField.validate = this.validateZipCode;
      this.syncPhoneValidity();

      // if we dont have customer data we still want to populate the first portion
    },
    setInputFields() {
      if (this.activeOrder.id && Object.keys(this.activeOrder).length > 0) {
        const product = this.activeOrder.products.find(
          prod => prod.id === parseInt(this.$route.params.typeId, 10)
        );
        if (product) {
          const orderProduct =
            'order_products' in product ? product.order_products[0] : {};

          this.product = product;
          this.orderProduct = orderProduct;
        }
        // check the order product and set it.
        // if (this.product.giftable) this.setCheck();
        this.setPartnerDataToShippingInfo();
        this.fieldRefs = {};
      }
    },
    triggerUpdate(field, val) {
      // this is used to trigger the update on the select field.
      // this is used for the country select field.
      if (field === 'state') {
        const countryField = this.fieldArrays.find(i => i.name === 'country');
        if (countryField) {
          const stateField = this.fieldArrays.find(i => i.name === 'state');
          const states = statesByCountry(val);
          stateField.items = states;
          if (!states.some(state => state.value === stateField.val))
            stateField.val = '';
        }
      }
    },
    valueChanged(event) {
      if (event.target.name === 'zip_code')
        this.validateZipCode(event.target.value);
    },
    validateZipCode(value) {
      const isInvalidValid = isInvalidZipCode(
        value,
        this.product.id,
        this.offer.zip_code_settings
      );
      this.modal.error = isInvalidValid;
    }
  },
  async created() {
    if (this.activeOrder.id) {
      await this.$api.orders.keepOrderAlive({
        orderId: this.activeOrder.id
      });

      // check if returning back to myself after shipping to someone if we get the distributed object in the response.

      this.shipToOther =
        'distribute' in this.activeOrder &&
        (!!this.activeOrder.distribute ||
          !!Object.keys(this.activeOrder.distribute).length > 0);
      // this.shipToOther = this.activeOrder?.distribute;
      this.setInputFields();
    }
  },
  watch: {
    shipToOther(newValue) {
      if (this.$refs.form) this.$refs.form.reset();
      this.setShip(newValue);
      this.$nextTick(() => {
        this.setPartnerDataToShippingInfo();
        
        this.$nextTick(() => {
          if (this.$refs.form) this.$refs.form.resetValidation();
        });
      });
    }
  }
};
</script>
<style lang="scss" scoped>
.shipping-title__btn {
  font-size: 0.875rem !important;
  font-weight: 500;
  letter-spacing: 0.0892857143em;
  min-height: 40px !important;
  height: 40px !important;
  border-radius: 20px !important;
}

:deep(.v-field--disabled) {
  opacity: 1;
}

:deep(.v-field--variant-outlined:not(.v-field--focused):not(.v-field--error) .v-field__outline) {
  color: rgba(var(--v-theme-on-surface), 1) !important;
}

:deep(.v-field--variant-outlined:not(.v-field--focused):not(.v-field--error):hover .v-field__outline) {
  color: rgba(var(--v-theme-on-surface), 1) !important;
}

.shipping-info-form :deep(.v-field__input) {
  color: rgba(var(--v-theme-on-surface), 1) !important;
  padding-left: 12px;
}

.shipping-info-form {
  :deep(.v-field--disabled .v-field__input) {
    opacity: 1 !important;
  }

  :deep(.v-field--disabled input) {
    color: rgba(var(--v-theme-on-surface), 0.38) !important;
    -webkit-text-fill-color: rgba(var(--v-theme-on-surface), 0.38) !important;
  }
}

:deep(.v-field--variant-outlined .v-label.v-field-label--floating) {
  background: rgb(var(--v-theme-surface)) !important;
  font-size: 12px !important;
}
/* Remove background when error + blurred */
.shipping-info-form :deep(
  .shipping-info-form__input 
  .v-field--error:not(.v-field--focused) 
  .v-field-label--floating
) {
  background: #ffffff !important;
  // color: rgb(var(--v-theme-error));
  padding: 0 4px !important;
  z-index: 2;
  position: relative;
  display: inline-block;
  top: -0px;
  left: -4px;
  font-size: 11px !important;
}

.shipping-info-form :deep(.shipping-info-form__input .v-field--error.v-field--variant-outlined .v-field__outline__start),
.shipping-info-form :deep(.shipping-info-form__input .v-field--error.v-field--variant-outlined .v-field__outline__end) {
  border-top-width: 2px !important;
  border-bottom-width: 2px !important;
  border-color: rgb(var(--v-theme-error)) !important;
}

.shipping-info-form :deep(.shipping-info-form__input .v-field--error.v-field--variant-outlined .v-field__outline__notch::before),
.shipping-info-form :deep(.shipping-info-form__input .v-field--error.v-field--variant-outlined .v-field__outline__notch::after) {
  border-top-width: 2px !important;
  border-bottom-width: 2px !important;
  border-color: rgb(var(--v-theme-error)) !important;
}

/* When focused, hide notch border color */
.shipping-info-form :deep(
  .shipping-info-form__input 
  .v-field--error.v-field--focused.v-field--variant-outlined 
  .v-field__outline__notch::before
),
.shipping-info-form :deep(
  .shipping-info-form__input 
  .v-field--error.v-field--focused.v-field--variant-outlined 
  .v-field__outline__notch::after
) {
  border-top-color: transparent !important;
}

.shipping-info-form :deep(.shipping-info-form__input .v-field--error.v-field--variant-outlined .v-field__outline__start) {
  border-left-width: 2px !important;
}

.shipping-info-form :deep(.shipping-info-form__input .v-field--error.v-field--variant-outlined .v-field__outline__end) {
  border-right-width: 2px !important;
}

/* Error border */
.shipping-info-form :deep(.shipping-info-form__input .v-field--error.v-field--variant-outlined) {
  --v-field-border-width: 2px;
  --v-field-border-opacity: 1;
}

/* Keep full red border even after blur */
.shipping-info-form :deep(
  .shipping-info-form__input
  .v-field--error.v-field--variant-outlined:not(.v-field--focused)
) {
  --v-field-border-width: 2px;
  --v-field-border-opacity: 1; /* This is the key */
}

:deep(.v-field--variant-outlined .v-field__outline__start) {
  flex: 0 0 16px !important;
}

.shipping-info-form :deep(.shipping-info-form__input .v-field) {
  --v-field-padding-start: 12px;
  --v-field-padding-end: 12px;
}

.shipping-info-form :deep(.shipping-info-form__input .v-input__details) {
  margin-top: 0px;
  padding-top: 0px;
  padding-left: 12px;
  letter-spacing: 0 !important;
  font-size: 10px !important;
}
:deep(.v-messages__message) {
  line-height: 5px;
}
</style>
<style lang="scss">
.shipping-info-form__select-menu .v-list-item--active {
  background-color: rgba(var(--v-theme-primary), 0.12) !important;
  color: rgba(var(--v-theme-primary), 1) !important;
}

.shipping-info-form__select-menu .v-list-item--active .v-list-item__overlay {
  opacity: 0 !important;
}

.shipping-info-form__select-menu .v-list-item--active:hover {
  background-color: rgba(var(--v-theme-primary), 0.12) !important;
}

.shipping-info-form__select-menu .v-list-item-title {
  font-size: 13px;
}

.shipping-info-form__checkbox {
  margin-top: -4px;
  margin-left: -5px;
  margin-bottom: 14px;
}

.shipping-info-form__checkbox .v-label {
  margin-inline-start: 6px;
  letter-spacing: normal !important;
  color: rgba(var(--v-theme-on-surface), 0.6) !important;
}

.shipping-info-form__checkbox
  .v-selection-control:not(.v-selection-control--dirty)
  .v-selection-control__input
  > .v-icon {
  color: rgb(var(--v-theme-on-surface)) !important;
  opacity: 1;
}

.shipping-info-form__checkbox
  .v-selection-control.v-selection-control--dirty
  .v-selection-control__input
  > .v-icon {
  color: rgb(var(--v-theme-primary)) !important;
}
</style>